import { useEffect, useState } from "react";
import {
  FormHelperText,
  Grid,
  Typography,
  Box,
  Chip,
  useTheme,
  Button,
  Stack,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import strings from "global/constants/StringConstants";
import history from "utils/history";
import ReactQuill from "react-quill";
import { makeStyles } from "@mui/styles";
import {
  addNewSupportTicket,
  getProcessDefinitions,
  uploadSupportTicketAttachment,
} from "./CreateTicketService";
import viewpaths from "global/constants/ViewPathConstants";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import CustomInput from "global/components/CustomInput/CustomInput";
import DropZoneIcon from "../../../assets/icons1/dropzone_icon.svg";
import ticketsStyles from "../Tickets.styles";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";

const useStyles = makeStyles(() => ({
  customQuill: {
    "& .ql-editor": {
      minHeight: "5em !important",
    },
    "& .ql-toolbar": {
      borderRadius: "10px 10px 0 0",
      border: " 0.5px solid #E9E9E9",
    },
    "& .ql-container.ql-snow": {
      borderRadius: "0 0 10px 10px ",
      border: " 0.5px solid #E9E9E9",
    },
  },
}));

const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
];

interface CustomProps {
  openModel: boolean;
  setOpenModel: Function;
  handleDialogClose: Function;
  getTableData: any;
  fetchMetrics: any;
}

const CreateTicket = (props: CustomProps) => {
  const theme = useTheme();
  const classes = ticketsStyles(theme);
  const editorClasses = useStyles();
  const [process, setProcess] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filePath, setFilePath] = useState<string[]>([]);
  const [uploadFileName, setUploadFileName] = useState<any>([]);
  const priorities = ["High", "Medium", "Low"];
  const [ticketForm, setTicketForm] = useState<any>({
    process: {
      value: "",
      error: "",
    },
    priority: {
      value: "",
      error: "",
    },
    title: {
      value: "",
      error: "",
    },
    description: {
      value: "",
      error: "",
    },
  });

  useEffect(() => {
    if (props.openModel) {
      loadProcess();
    }
  }, [props.openModel]);

  useEffect(() => {
    setTicketForm({
      process: {
        value: "",
        error: "",
      },
      priority: {
        value: "",
        error: "",
      },
      title: {
        value: "",
        error: "",
      },
      description: {
        value: "",
        error: "",
      },
    });
    setFilePath([]);
    setUploadFileName([]);
  }, [props.openModel]);

  const handleOnChange = (event: any) => {
    setTicketForm({
      ...ticketForm,
      [event.target.name]: {
        ...ticketForm[event.target.name],
        value: event.target.value,
        error: false,
      },
    });
  };

  const handleOnSelect = (keyValue: string, newValue: any) => {
    setTicketForm((prevForm: any) => ({
      ...prevForm,
      [keyValue]: {
        ...prevForm[keyValue],
        value: newValue?.label || "",
        error: false,
      },
    }));
  };

  const handleContentChange = (content: string) => {
    setTicketForm({
      ...ticketForm,
      description: {
        value: content,
        error: "",
      },
    });
  };

  const handleDropRejected = (files: any, event: any) => {
    const MAX_FILE_SIZE = 2 * 1024 * 1024;
    const rejectedFiles = files.filter(
      (file: any) => file.size > MAX_FILE_SIZE
    );
    if (rejectedFiles.length > 0) {
      const rejectedFileNames = rejectedFiles
        .map((file: any) => file.name)
        .join(", ");
      openErrorNotification(
        `The following files exceed the maximum allowed size of 2 MB: ${rejectedFileNames}`
      );
    }
  };

  const handleValidation = () => {
    let errors = ticketForm;
    let isValid = true;
    const process = ticketForm.process.value;
    const title = ticketForm.title.value.trim();
    const description = ticketForm.description.value.trim();
    const priority = ticketForm.priority.value.trim();
    if (!process && !title && !description && !priority) {
      // Set the fields as error true
      errors.process.error = "Please select category";
      errors.title.error = "Please enter summary";
      errors.priority.error = "Please select priority";
      errors.description.error = "Please enter description";
      isValid = false;
    } else if (!process) {
      errors.process.error = "Please select category";
      isValid = false;
    } else if (!priority) {
      errors.priority.error = "Please select priority";
      isValid = false;
    } else if (!title) {
      errors.title.error = "Please enter summary";
      isValid = false;
    } else if (!description) {
      errors.description.error = "Please enter description";
      isValid = false;
    } else if (filePath.length > 10) {
      isValid = false;
      openErrorNotification("You can upload maximum 10 files only.");
    }
    setTicketForm({ ...errors });
    return isValid;
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      if (handleValidation() && ticketForm?.description?.value?.length < 500) {
        const body = {
          process: ticketForm.process.value,
          name: ticketForm.title.value,
          description: ticketForm.description.value,
          docUrls: filePath,
          priority: ticketForm.priority.value,
        };
        await addNewSupportTicket(body);
        openSuccessNotification("Your Response has been submitted");
        setTicketForm({
          process: {
            value: "",
            error: "",
          },
          priority: {
            value: "",
            error: "",
          },
          title: {
            value: "",
            error: "",
          },
          description: {
            value: "",
            error: "",
          },
        });
        setFilePath([]);
        setUploadFileName([]);
        setIsLoading(false);
        props.setOpenModel(false);
        await props.fetchMetrics();
        await props.getTableData();
      } else {
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleDeleteFile = (index: any) => {
    setUploadFileName(
      uploadFileName.filter((item: any, i: number) => i !== index)
    );
    setFilePath(filePath.filter((item: any, i: number) => i !== index));
  };

  const uploadFile = async (event: any) => {
    try {
      let fileNameArray: any[] = [...uploadFileName];
      let uploadFilePath: any[] = [...filePath];
      const fileData = event?.filter((data: any) => {
        return !uploadFileName?.includes(data?.file?.name);
      });
      const isExits = checkIsExitsFileName(fileData);
      if (fileData.length + uploadFilePath.length > 10) {
        return openErrorNotification("Maximum 10 file can be added.");
      }
      if (!isExits) {
        for (const element of fileData) {
          setIsLoading(true);
          let fileNames = element?.name;
          const data = new FormData();
          data.append("file", element);
          const [res] = await Promise.all([
            uploadSupportTicketAttachment(data, "new"),
          ]);
          uploadFilePath.push(res?.path);
          fileNameArray.push(fileNames);
          setIsLoading(false);
        }
        setFilePath(uploadFilePath);
        setUploadFileName(fileNameArray);
      }
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };
  const checkIsExitsFileName = (files: any) => {
    const data: any = [];
    return files.every((item: any) => {
      if (uploadFileName?.includes!(item?.file?.name)) {
        data.push(item?.file?.name);
      }
      return uploadFileName?.includes(item?.file?.name);
    });
  };

  const loadProcess = async () => {
    try {
      setIsLoading(true);
      const response = await getProcessDefinitions();
      setProcess([...response]);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const supportTicketContent = () => {
    return (
      <>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid xs={12} sm={12} md={6} lg={6} xl={6} item>
            <Box>
              <CustomAutoComplete
                id="group-autocomplete"
                required
                name="process"
                label="Category "
                placeHolder="Select category "
                textFieldVariant="outlined"
                options={process.map((process: any) => ({
                  label: process.name,
                  id: process.id,
                }))}
                value={ticketForm.process.value}
                onChange={(event: any, newValue: any) =>
                  handleOnSelect("process", newValue)
                }
                errorMessage={
                  !isTruthy(ticketForm.process.value) &&
                  ticketForm.process.error
                    ? ticketForm.process.error
                    : ""
                }
              />
            </Box>
          </Grid>

          <Grid xs={12} sm={12} md={6} lg={6} xl={6} item>
            <Box>
              <CustomAutoComplete
                id="group-autocomplete"
                required
                label="Priority"
                name="priority"
                placeHolder="Select priority"
                textFieldVariant="outlined"
                options={priorities.map((priority: any) => ({
                  label: priority,
                  id: priority.id,
                }))}
                value={ticketForm.priority.value}
                onChange={(event: any, newValue: any) =>
                  handleOnSelect("priority", newValue)
                }
                errorMessage={
                  !isTruthy(ticketForm.priority.value) &&
                  ticketForm.priority.error
                    ? ticketForm.priority.error
                    : ""
                }
              />
            </Box>
          </Grid>
          <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
            <CustomInput
              required
              id="title"
              placeHolder="Enter summary of ticket"
              type="text"
              name="title"
              label="Summary"
              onChange={handleOnChange}
              value={ticketForm.title.value}
              propsToInputElement={{ minLength: 5, maxLength: 100 }}
              error={
                !isTruthy(ticketForm.title.value) && ticketForm.title.error
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {ticketForm.title.value.length} / 100
                    </span>
                  </InputAdornment>
                ),
              }}
            />
            {!isTruthy(ticketForm.title.value) && ticketForm.title.error && (
              <FormHelperText error>{ticketForm.title.error}</FormHelperText>
            )}
          </Grid>

          <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
            <Box display={"flex"} flexDirection={"column"}>
              <Box mb={1}>
                <InputLabel sx={classes.headingText} required id="description">
                  Description
                </InputLabel>
              </Box>
              <Box
                sx={{
                  border:
                    !isTruthy(ticketForm.description.value) &&
                    ticketForm.description.error
                      ? "0.5px solid red"
                      : "0.5px solid #E9E9E9",
                  borderRadius: "10px",
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={ticketForm.description.value}
                  onChange={handleContentChange}
                  formats={formats}
                  modules={modules}
                  className={editorClasses.customQuill}
                />
              </Box>

              {!isTruthy(ticketForm.description.value) &&
                ticketForm.description.error && (
                  <FormHelperText error>
                    {ticketForm.description.error}
                  </FormHelperText>
                )}
              {isTruthy(ticketForm.description.value) &&
                ticketForm.description.value.length >=
                  strings.USER_DESCRIPTION_LIMIT && (
                  <FormHelperText error>
                    {`Description cannot be more than ${strings.USER_DESCRIPTION_LIMIT} characters`}
                  </FormHelperText>
                )}
            </Box>
          </Grid>
          <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
            <Box>
              <Typography sx={classes.headingText} mb={1}>
                {" "}
                Upload File{" "}
              </Typography>
              <Box mb={1}>
                <CustomDropzone
                  sx={{ height: "8rem" }}
                  onAdd={(file: any) => {
                    uploadFile(file);
                  }}
                  maxFileSize={2097152}
                  showAlerts={false}
                  multipleFileUpload={false}
                  dropzoneText={"Drop file here or click to upload"}
                  icon={DropZoneIcon}
                  acceptedFiles={[
                    ".jpeg",
                    ".jpg",
                    ".png",
                    ".doc",
                    ".pdf",
                    ".csv",
                    ".xls",
                    ".xlsx",
                  ]}
                />
                <Grid container spacing={1} mt={1}>
                  {uploadFileName &&
                    uploadFileName
                      ?.filter(
                        (item1: any, index: number) =>
                          uploadFileName.indexOf(item1) === index
                      )
                      ?.map((item: any, index2: number) => {
                        return (
                          <>
                            <Grid item>
                              <Chip
                                label={item}
                                variant="filled"
                                onDelete={() => handleDeleteFile(index2)}
                              />
                            </Grid>
                          </>
                        );
                      })}
                </Grid>
              </Box>
              <Typography variant="subtitle2">
                (Supported file formats: jpeg, jpg, png, gif, tiff, pdf, csv,
                doc, docx, txt, xls, xlsx, heic)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={2}>
        <Button
          id="contacts_add_save_btn"
          variant="contained"
          onClick={() => handleSubmit()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Create
        </Button>
        <Button
          id="contacts_add__cancel_btn"
          variant="outlined"
          onClick={() => props.handleDialogClose()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Cancel
        </Button>
      </Stack>
    );
  };

  const supportTicketModal = () => {
    return (
      <>
        <CustomDialog
          handleDialogClose={props.handleDialogClose}
          isDialogOpen={props.openModel}
          body={supportTicketContent()}
          footer={getFooter()}
          title={<Typography variant="h4">Create Ticket</Typography>}
          closeButtonVisibility
          closable
          maxWidth="md"
          persistOnClickOutside
          persistOnEsc
        />
      </>
    );
  };

  return (
    <Box>
      {supportTicketModal()}
      <CustomLoader isLoading={isLoading} />
    </Box>
  );
};

export default CreateTicket;
